import { Route, Station, Project } from '../types';

interface Position {
  lat: number;
  lon: number;
}

export function calculateHaversineDistance(pos1: Position, pos2: Position, routeFactor: number = 1.1): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRad(pos2.lat - pos1.lat);
  const dLon = toRad(pos2.lon - pos1.lon);
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(toRad(pos1.lat)) * Math.cos(toRad(pos2.lat)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const straightLineDistance = R * c; // Returns distance in kilometers
  return straightLineDistance * routeFactor; // Apply route factor (1.1 for trains, 1.0 for flights)
}

export function getEffectiveDistance(route: Route, project: Project, shouldWarn: boolean = false): number {
  // Always calculate and store automatic distance if coordinates are available
  const sourceStation = project.network.stations.find(s => s.id === route.source);
  const targetStation = project.network.stations.find(s => s.id === route.target);

  if (sourceStation?.latitude && sourceStation?.longitude && 
      targetStation?.latitude && targetStation?.longitude) {
    const calculatedDistance = calculateHaversineDistance(
      { lat: sourceStation.latitude, lon: sourceStation.longitude },
      { lat: targetStation.latitude, lon: targetStation.longitude }
    );
    // Store the automatic distance as an integer
    route.automaticDistance = Math.round(calculatedDistance);
  }

  // Return manual distance if in manual mode
  if (route.distanceCalculation === 'manual') {
    return route.distance;
  }

  // For automatic mode, return automatic distance if available, otherwise fall back to manual
  if (!route.automaticDistance) {
    if (shouldWarn) {
      const missingCoordinates = [];
      if (!sourceStation?.latitude || !sourceStation?.longitude) {
        missingCoordinates.push(sourceStation?.name || 'unknown source station');
      }
      if (!targetStation?.latitude || !targetStation?.longitude) {
        missingCoordinates.push(targetStation?.name || 'unknown target station');
      }
      console.warn(
        `Route ${route.id} cannot use automatic distance calculation because ` +
        `${missingCoordinates.join(' and ')} ${missingCoordinates.length > 1 ? 'are' : 'is'} missing GPS coordinates.`
      );
    }
    return route.distance;
  }

  return route.automaticDistance;
}

export function updateAutomaticDistancesForStation(stationId: string, project: Project): void {
  const station = project.network.stations.find(s => s.id === stationId);
  if (!station?.latitude || !station?.longitude) return;

  // Find only the routes directly connected to this station
  const connectedRoutes = project.network.routes.filter(
    route => route.source === stationId || route.target === stationId
  );

  // Update only the connected routes that use automatic calculation
  connectedRoutes.forEach(route => {
    if (route.distanceCalculation === 'automatic') {
      const effectiveDistance = getEffectiveDistance(route, project); // Returns distance in kilometers
      route.automaticDistance = Math.round(effectiveDistance); // Store as integer
    }
  });
}

function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}

export function getEdgeLabel(route: Route, project: Project): string {
  const effectiveDistance = getEffectiveDistance(route, project);
  return `${Math.ceil(effectiveDistance).toLocaleString()} km`;
} 